import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import {
  approvePhoto,
  approvePhotos,
  countPendingPhotos,
  deletePhoto,
  listApprovedPhotosAdmin,
  listPendingPhotos,
  rejectPhoto,
  rejectPhotos,
} from '@/lib/admin';
import { useDisplayName } from '@/hooks/useDisplayName';
import { telegramFileUrl } from '@/lib/telegram';
import type { PhotoRow } from '@/types';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Toast } from '@/components/ui/Toast';

function errorToString(err: unknown): string {
  if (err == null) return 'Eroare necunoscută';
  if (err instanceof Error) return err.message;
  if (typeof err === 'object') {
    const obj = err as Record<string, unknown>;
    if (typeof obj.message === 'string') return obj.message;
    try {
      return JSON.stringify(err);
    } catch {
      return 'Eroare (obiect nereprezentabil)';
    }
  }
  return String(err);
}

export default function ModerationPage() {
  const { role, loading: authLoading, logout } = useAdminAuth();
  const { name: displayName } = useDisplayName();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [activeTab, setActiveTab] = useState<'pending' | 'approved'>('pending');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [rejectModalFor, setRejectModalFor] = useState<PhotoRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [deleteModalFor, setDeleteModalFor] = useState<PhotoRow | null>(null);

  // Redirect la /admin daca nu suntem admin
  useEffect(() => {
    if (!authLoading && role === null) {
      navigate('/admin', { replace: true });
    }
  }, [authLoading, role, navigate]);

  const { data: pending = [], isLoading: pendingLoading } = useQuery({
    queryKey: ['pending-photos'],
    queryFn: listPendingPhotos,
    enabled: role !== null,
    refetchInterval: 10_000,
  });

  const { data: approved = [], isLoading: approvedLoading } = useQuery({
    queryKey: ['approved-photos-admin'],
    queryFn: listApprovedPhotosAdmin,
    enabled: role !== null,
    refetchInterval: 15_000,
  });

  const { data: pendingCount = 0 } = useQuery({
    queryKey: ['pending-count'],
    queryFn: countPendingPhotos,
    enabled: role !== null,
    refetchInterval: 10_000,
  });

  // Toggle selectia unui item
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Select all / Deselect all
  const toggleSelectAll = () => {
    if (selectedIds.size === pending.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pending.map((p) => p.id)));
    }
  };

  const invalidateAll = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['pending-photos'] });
    qc.invalidateQueries({ queryKey: ['approved-photos-admin'] });
    qc.invalidateQueries({ queryKey: ['pending-count'] });
    qc.invalidateQueries({ queryKey: ['gallery'] });
  }, [qc]);

  // Aprobare individuală
  const onApprove = useCallback(
    async (photo: PhotoRow) => {
      try {
        await approvePhoto(photo.id, displayName || role || undefined);
        invalidateAll();
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(photo.id);
          return next;
        });
        setInfo(`Poză aprobată ✓`);
      } catch (e) {
        setError(errorToString(e) || 'Eroare la aprobare');
      }
    },
    [invalidateAll, displayName, role],
  );

  // Aprobare multiplă (în masă)
  const onApproveBulk = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await approvePhotos(ids, displayName || role || undefined);
      invalidateAll();
      setSelectedIds(new Set());
      setInfo(`${ids.length} poze aprobate cu succes ✓`);
    } catch (e) {
      setError(errorToString(e) || 'Eroare la aprobarea în masă');
    }
  }, [selectedIds, invalidateAll, displayName, role]);

  // Respingere multiplă (în masă)
  const onRejectBulk = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      await rejectPhotos(ids, displayName || role || undefined, rejectReason.trim() || undefined);
      invalidateAll();
      setSelectedIds(new Set());
      setBulkRejectOpen(false);
      setRejectReason('');
      setInfo(`${ids.length} poze respinse`);
    } catch (e) {
      setError(errorToString(e) || 'Eroare la respingerea în masă');
    }
  }, [selectedIds, rejectReason, invalidateAll, displayName, role]);

  // Respingere individuală
  const onConfirmReject = useCallback(async () => {
    if (!rejectModalFor) return;
    try {
      await rejectPhoto(rejectModalFor.id, displayName || role || undefined, rejectReason.trim() || undefined);
      invalidateAll();
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(rejectModalFor.id);
        return next;
      });
      setInfo(`Poză respinsă`);
      setRejectModalFor(null);
      setRejectReason('');
    } catch (e) {
      setError(errorToString(e) || 'Eroare la respingere');
    }
  }, [rejectModalFor, rejectReason, invalidateAll, displayName, role]);

  // Ștergere definitivă din galerie
  const onConfirmDelete = useCallback(async () => {
    if (!deleteModalFor) return;
    try {
      await deletePhoto(deleteModalFor.id);
      invalidateAll();
      setInfo(`Poza a fost ștearsă din galerie.`);
      setDeleteModalFor(null);
    } catch (e) {
      setError(errorToString(e) || 'Eroare la ștergerea pozei');
    }
  }, [deleteModalFor, invalidateAll]);

  if (authLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-accent" />
      </main>
    );
  }

  return (
    <main className="min-h-screen px-md py-lg bg-bg pb-24">
      {/* Header */}
      <div className="container-page flex items-end justify-between gap-md pb-md border-b border-border">
        <div>
          <p className="eyebrow">Panou Moderare</p>
          <h1 className="font-serif text-h2 text-fg">Gestionare Fotografii</h1>
          <p className="mt-xs text-sm text-fg-soft">
            Autentificat ca <span className="font-mono text-fg font-medium">{role}</span>
          </p>
        </div>
        <div className="flex gap-sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/gallery')}
            title="Înapoi la galerie"
          >
            ← Vezi galeria
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              await logout();
              navigate('/admin');
            }}
          >
            Delogare
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="container-page mt-md flex items-center justify-between gap-md">
        <div className="flex gap-xs rounded-lg bg-bg-alt p-1 border border-border">
          <button
            type="button"
            onClick={() => {
              setActiveTab('pending');
              setSelectedIds(new Set());
            }}
            className={`flex items-center gap-2 rounded-md px-md py-1.5 text-sm font-medium transition-all ${
              activeTab === 'pending'
                ? 'bg-accent text-bg-alt shadow-sm'
                : 'text-fg-soft hover:text-fg'
            }`}
          >
            <span>În așteptare</span>
            <span
              className={`rounded-full px-1.5 py-0.5 text-xs font-bold ${
                activeTab === 'pending' ? 'bg-bg text-accent' : 'bg-border text-fg-soft'
              }`}
            >
              {pendingCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab('approved');
              setSelectedIds(new Set());
            }}
            className={`flex items-center gap-2 rounded-md px-md py-1.5 text-sm font-medium transition-all ${
              activeTab === 'approved'
                ? 'bg-accent text-bg-alt shadow-sm'
                : 'text-fg-soft hover:text-fg'
            }`}
          >
            <span>Aprobate în galerie</span>
            <span
              className={`rounded-full px-1.5 py-0.5 text-xs font-bold ${
                activeTab === 'approved' ? 'bg-bg text-accent' : 'bg-border text-fg-soft'
              }`}
            >
              {approved.length}
            </span>
          </button>
        </div>

        {/* Bulk select action (only in pending) */}
        {activeTab === 'pending' && pending.length > 0 && (
          <div className="flex items-center gap-sm">
            <Button variant="ghost" size="sm" onClick={toggleSelectAll}>
              {selectedIds.size === pending.length ? 'Deselectează tot' : 'Selectează tot'}
            </Button>
          </div>
        )}
      </div>

      {/* Content Tab 1: PENDING */}
      {activeTab === 'pending' && (
        <div className="mt-md">
          {pendingLoading ? (
            <div className="container-page grid grid-cols-2 gap-md sm:grid-cols-3 md:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="skeleton aspect-[3/4] rounded-md" />
              ))}
            </div>
          ) : pending.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="container-page flex flex-col items-center justify-center py-2xl text-center"
            >
              <p className="font-serif text-h3 text-fg">Gata — nicio poză în așteptare</p>
              <p className="mt-sm text-fg-soft">Când invitații încarcă poze noi, vor apărea aici pentru aprobare.</p>
            </motion.div>
          ) : (
            <div className="container-page grid grid-cols-2 gap-md sm:grid-cols-3 md:grid-cols-4">
              <AnimatePresence mode="popLayout">
                {pending.map((photo) => {
                  const isChecked = selectedIds.has(photo.id);
                  return (
                    <motion.article
                      key={photo.id}
                      layout
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.92 }}
                      transition={{ duration: 0.2 }}
                      onClick={() => toggleSelect(photo.id)}
                      className={`event-card flex flex-col cursor-pointer transition-all border ${
                        isChecked
                          ? 'border-accent ring-2 ring-accent/30 shadow-md'
                          : 'border-border hover:border-fg-soft'
                      }`}
                    >
                      <div className="relative aspect-[3/4] overflow-hidden bg-bg">
                        {/* Checkbox */}
                        <div
                          className="absolute top-2 left-2 z-10 rounded bg-black/60 p-1 backdrop-blur-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelect(photo.id)}
                            className="h-4 w-4 rounded border-border text-accent focus:ring-accent cursor-pointer"
                          />
                        </div>

                        {photo.telegram_file_id ? (
                          <img
                            src={telegramFileUrl(photo.telegram_file_id)}
                            alt={photo.caption || `Poză de ${photo.uploaded_by}`}
                            loading="lazy"
                            className="h-full w-full object-contain bg-bg"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-fg-soft">
                            Fără thumbnail
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-xs p-sm">
                        <p className="truncate text-xs font-medium text-fg">
                          {photo.uploaded_by}
                        </p>
                        {photo.caption && (
                          <p className="clamp-2 text-xs text-fg-soft">{photo.caption}</p>
                        )}
                        <div
                          className="mt-xs flex gap-xs pt-1 border-t border-border/50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            variant="primary"
                            size="sm"
                            fullWidth
                            onClick={() => onApprove(photo)}
                          >
                            Aprobă
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            fullWidth
                            onClick={() => {
                              setRejectModalFor(photo);
                              setRejectReason('');
                            }}
                          >
                            Respinge
                          </Button>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      )}

      {/* Content Tab 2: APPROVED IN GALLERY */}
      {activeTab === 'approved' && (
        <div className="mt-md">
          {approvedLoading ? (
            <div className="container-page grid grid-cols-2 gap-md sm:grid-cols-3 md:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="skeleton aspect-[3/4] rounded-md" />
              ))}
            </div>
          ) : approved.length === 0 ? (
            <div className="container-page py-2xl text-center">
              <p className="font-serif text-h3 text-fg">Nicio poză aprobată încă</p>
              <p className="mt-sm text-fg-soft">Aprobă poze din tab-ul „În așteptare” pentru a le afișa în galerie.</p>
            </div>
          ) : (
            <div className="container-page grid grid-cols-2 gap-md sm:grid-cols-3 md:grid-cols-4">
              {approved.map((photo) => (
                <div
                  key={photo.id}
                  className="event-card flex flex-col border border-border overflow-hidden rounded-md bg-bg-alt"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-bg">
                    {photo.telegram_file_id ? (
                      <img
                        src={telegramFileUrl(photo.telegram_file_id)}
                        alt={photo.caption || ''}
                        loading="lazy"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs text-fg-soft">
                        Fără imagine
                      </div>
                    )}
                    <span className="absolute top-2 right-2 rounded bg-accent/90 text-bg-alt text-[10px] px-1.5 py-0.5 font-mono">
                      În galerie
                    </span>
                  </div>

                  <div className="p-sm flex flex-col justify-between flex-1 gap-sm">
                    <div>
                      <p className="text-xs font-semibold text-fg truncate">{photo.uploaded_by}</p>
                      {photo.caption && (
                        <p className="text-xs text-fg-soft clamp-2 mt-0.5">{photo.caption}</p>
                      )}
                      <p className="text-[10px] font-mono text-fg-soft mt-1">
                        Aprobat la: {photo.reviewed_at ? new Date(photo.reviewed_at).toLocaleDateString('ro-RO') : '-'}
                      </p>
                    </div>

                    <Button
                      variant="secondary"
                      size="sm"
                      fullWidth
                      className="border-error/40 text-error hover:bg-error/10 hover:border-error"
                      onClick={() => setDeleteModalFor(photo)}
                    >
                      🗑️ Șterge din galerie
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Floating Action Bar for Bulk Moderation */}
      <AnimatePresence>
        {activeTab === 'pending' && selectedIds.size > 0 && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-6 inset-x-0 z-50 flex justify-center px-md pointer-events-none"
          >
            <div className="pointer-events-auto flex items-center gap-md rounded-full bg-fg text-bg px-lg py-sm shadow-2xl border border-border/20">
              <span className="text-sm font-medium">
                {selectedIds.size} {selectedIds.size === 1 ? 'poză selectată' : 'poze selectate'}
              </span>

              <div className="flex gap-sm">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onApproveBulk}
                  className="bg-accent text-bg-alt hover:brightness-110"
                >
                  ✓ Aprobă toate ({selectedIds.size})
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setBulkRejectOpen(true);
                    setRejectReason('');
                  }}
                  className="bg-bg/20 text-bg border-bg/30 hover:bg-bg/30"
                >
                  ✕ Respinge ({selectedIds.size})
                </Button>
                <button
                  type="button"
                  onClick={() => setSelectedIds(new Set())}
                  className="text-xs text-bg/60 hover:text-bg underline px-2"
                >
                  Anulează
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reject modal (single) */}
      <AnimatePresence>
        {rejectModalFor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-overlay flex items-center justify-center bg-black/50 p-md backdrop-blur-sm"
            onClick={() => setRejectModalFor(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="w-full max-w-md rounded-lg bg-bg-alt p-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="eyebrow mb-sm">Respinge poza</p>
              <h2 className="font-serif text-h3 mb-md text-fg">
                Respingi poza lui {rejectModalFor.uploaded_by}?
              </h2>
              <p className="mb-md text-sm text-fg-soft">
                Motivul e opțional — te ajută să-ți amintești de ce ai respins.
              </p>
              <Input
                label="Motiv (opțional)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <div className="mt-lg flex justify-end gap-sm">
                <Button variant="ghost" size="sm" onClick={() => setRejectModalFor(null)}>
                  Anulează
                </Button>
                <Button variant="primary" size="sm" onClick={onConfirmReject}>
                  Respinge
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Reject Modal */}
      <AnimatePresence>
        {bulkRejectOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-overlay flex items-center justify-center bg-black/50 p-md backdrop-blur-sm"
            onClick={() => setBulkRejectOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="w-full max-w-md rounded-lg bg-bg-alt p-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="eyebrow mb-sm">Respingere în masă</p>
              <h2 className="font-serif text-h3 mb-md text-fg">
                Respingi {selectedIds.size} poze selectate?
              </h2>
              <Input
                label="Motiv (opțional)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <div className="mt-lg flex justify-end gap-sm">
                <Button variant="ghost" size="sm" onClick={() => setBulkRejectOpen(false)}>
                  Anulează
                </Button>
                <Button variant="primary" size="sm" onClick={onRejectBulk}>
                  Confirmă respingerea
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete from gallery modal */}
      <AnimatePresence>
        {deleteModalFor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-overlay flex items-center justify-center bg-black/50 p-md backdrop-blur-sm"
            onClick={() => setDeleteModalFor(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="w-full max-w-md rounded-lg bg-bg-alt p-lg shadow-xl border border-error/30"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="eyebrow mb-sm text-error">Ștergere din galerie</p>
              <h2 className="font-serif text-h3 mb-sm text-fg">
                Ești sigur că vrei să elimini această poză?
              </h2>
              <p className="mb-md text-sm text-fg-soft">
                Poza lui <strong className="text-fg">{deleteModalFor.uploaded_by}</strong> nu va mai apărea în galeria publică pentru invitați.
              </p>
              <div className="mt-lg flex justify-end gap-sm">
                <Button variant="ghost" size="sm" onClick={() => setDeleteModalFor(null)}>
                  Anulează
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onConfirmDelete}
                  className="bg-error hover:bg-error/90 text-white"
                >
                  Șterge definitiv
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {info && <Toast message={info} type="success" onClose={() => setInfo(null)} />}
      {error && <Toast message={error} type="error" onClose={() => setError(null)} />}
    </main>
  );
}
